/**
 * Activities (Navy Phase 0 handoff, "Tables" and stories 1, 18, 19, 21, 27
 * and 29): the trail of everything done in the workspace, beside the token
 * usage it is billed against. Phase 0 has no separate Audit Log, so this is it.
 *
 * There is no activity API yet, so the model holds the entries itself. It
 * seeds the trail the seeded Library and assessments imply and, once `watch`
 * is called, writes one entry per transition in either model ("Every
 * transition writes to Activities"), so neither of them has to know the log
 * exists. Anything that isn't a change of state, such as a report download,
 * calls `log`.
 */
import { formatTimestamp } from '../shared/datetime/timestamps';
import { LIBRARY_TYPES, seedLibraryScenarios } from './LibraryModel';
import { ASSESSMENT_STATUS } from './AssessmentsModel';

/* The same three categories token usage reports on. A scenario is part of the
   assessment story for now, and everything administrative reads as System. */
export const ACTIVITY_CATEGORIES = ['Assessments', 'Library', 'System'];

/** Each category's chip: the same tokens the usage bar uses. */
export const ACTIVITY_CATEGORY_CHIP = { Assessments: 'data', Library: 'automations', System: 'system' };

export function activityCategory(entry) {
  const action = String(entry.action || '').toLowerCase();
  if (/document|library/.test(action)) return 'Library';
  if (/scenario|assessment|upload/.test(action)) return 'Assessments';
  return 'System';
}

/* Usage and Status hold one value each for now (the prototype's ACT_VALUE),
   and still sort, so they behave like any other column. */
export const activityUsage = () => 'Not applicable';
export const activityStatus = () => 'Succeeded';

export const ACTIVITY_RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

export const DEFAULT_ACTIVITY_FILTERS = { search: '', range: '30', category: 'all' };
export const DEFAULT_ACTIVITY_SORT = { key: 'time', dir: 'desc' }; // Time, newest first

/**
 * The token usage card's figures, as the prototype shows them (NAVY.USAGE).
 * There is no metering for Navy yet; the platform's usage API replaces these.
 */
export const ACTIVITY_USAGE = {
  org: {
    total: '29.00M',
    window: 'last 30 days',
    allowanceLabel: 'Organization allowance (Sep 1 – Sep 30, 2026)',
    used: '29.00M',
    allowance: '25M',
    over: true,
    parts: [
      { label: 'Assessments', value: '22.88M', pct: 79, chip: 'data' },
      { label: 'Library', value: '1.49M', pct: 5, chip: 'automation' },
      { label: 'System', value: '4.63M', pct: 16, chip: 'system' },
      { label: 'Remaining', value: '0', pct: 0, chip: null },
    ],
    pages: '5966 pages',
    runs: '778 runs',
  },
  you: {
    total: '2.22M',
    window: 'last 30 days',
    allowanceLabel: 'Your allowance (Sep 1 – Sep 30, 2026)',
    used: '2.22M',
    allowance: '20M',
    over: false,
    parts: [
      { label: 'Assessments', value: '1.61M', pct: 8, chip: 'data' },
      { label: 'Library', value: '0.20M', pct: 1, chip: 'automation' },
      { label: 'System', value: '0.41M', pct: 2, chip: 'system' },
      { label: 'Remaining', value: '17.78M', pct: 89, chip: null },
    ],
    pages: '412 pages',
    runs: '63 runs',
  },
};

const MINUTE = 60000;
const DAY = 86400000;

// Who the seeded trail names. Role titles, not people: Phase 0 stores nobody
// beyond the signed-in evaluator (handoff questions.md, 7).
const SEED_ACTOR = 'Training Standards Officer';
const EVALUATOR = 'Evaluator';
const SYSTEM = 'System';
// The two documents the prototype's seed has the officer upload; the rest are the evaluator's.
const SEED_UPLOADERS = { d1: SEED_ACTOR, d4: SEED_ACTOR };
// The prototype's scenarios were saved three and two days back (NAVY.seed).
const SCENARIO_AGE = { s1: 3 * DAY, s2: 3 * DAY, s3: 2 * DAY };

const typeLabel = (doc) => LIBRARY_TYPES[doc.type]?.label || 'Document';

/** What an assessment's entries name: its scenario, and the reference key it's compared against. */
function scenarioLookups(documents, scenarios) {
  const scenario = (id) => scenarios.find((s) => s.id === id);
  return {
    scenarioName: (id) => scenario(id)?.name || '-',
    referenceKey: (id) => documents.find((d) => d.id === scenario(id)?.referenceKeyIds?.[0])?.name || 'the reference key',
  };
}
const statusLabel = (status) => ASSESSMENT_STATUS[status]?.label || status;
const at = (iso, minutes) => new Date(Date.parse(iso) + minutes * MINUTE).toISOString();

/**
 * The trail the seeded records imply, newest first. It opens with the first
 * sign-in, so the log is never empty, and carries the whole ladder each record
 * walked, not just where it rests (NAVY.seed). Uploading isn't seeded, so a
 * seeded assessment's ladder is fixed by its status.
 */
export function seedActivities({ documents = [], scenarios = [], assessments = [], now = Date.now() } = {}) {
  const trail = [];
  const add = (time, action, target, details, actor = SEED_ACTOR) => trail.push({ time, action, target, details, actor });
  const { scenarioName, referenceKey } = scenarioLookups(documents, scenarios);

  const earliest = documents.reduce((min, d) => Math.min(min, Date.parse(d.uploadedAt)), now);
  add(new Date(earliest - 60 * MINUTE).toISOString(), 'Signed in', SEED_ACTOR, 'First sign-in to this workspace');

  documents.forEach((d) => {
    const by = SEED_UPLOADERS[d.id] || EVALUATOR;
    add(d.uploadedAt, 'Library document uploaded', d.name, `${typeLabel(d)} · ${d.fileName}`, by);
    if (d.status === 'failed') add(at(d.uploadedAt, 2), 'Library document upload failed', d.name, 'Session expired during upload', by);
    if (d.archived) add(at(d.uploadedAt, DAY / MINUTE), 'Library document archived', d.name, d.fileName, by);
  });

  scenarios.forEach((s) => add(new Date(now - (SCENARIO_AGE[s.id] || 3 * DAY)).toISOString(),
    'Scenario created', s.name, 'Reference key, SOP and narrative selected'));

  assessments.forEach((a) => {
    const scenario = scenarioName(a.scenarioId);
    add(a.uploadedAt, 'Assessment created', a.name, `Uploading · ${scenario} · ${a.fileName}`, EVALUATOR);
    if (a.status === 'failed' && a.failedStep === 'upload') {
      add(at(a.uploadedAt, 11), 'Assessment upload failed', a.name, a.error, EVALUATOR);
      return;
    }
    add(at(a.uploadedAt, 11), 'Assessment upload complete', a.name, `Queued · ${scenario} · ${a.fileName}`, EVALUATOR);
    if (a.status === 'queued') return;
    add(at(a.uploadedAt, 12), 'Assessment processing started', a.name, `Comparing against ${referenceKey(a.scenarioId)}`, SYSTEM);
    if (a.status === 'processing') return;
    if (a.status === 'failed') {
      add(at(a.uploadedAt, 19), 'Assessment processing failed', a.name, a.error, SYSTEM);
      return;
    }
    add(at(a.uploadedAt, 24), 'Assessment processing complete', a.name, 'Transcript, comparison and report ready', SYSTEM);
  });

  return trail
    .sort((x, y) => y.time.localeCompare(x.time))
    .map((entry, i) => ({ id: `act${trail.length - i}`, ...entry }));
}

/* ---- Transitions ----------------------------------------------------------
   Each pair of snapshots becomes the entries for what changed between them,
   worded as the prototype logs them. Progress and run time aren't events. */

const byId = (list) => new Map((list || []).map((x) => [x.id, x]));

/** The Library's documents and scenarios, before and after one change. */
export function libraryEvents(prev, next) {
  const events = [];
  const before = byId(prev.documents);
  next.documents.forEach((d) => {
    const was = before.get(d.id);
    const details = `${typeLabel(d)} · ${d.fileName}`;
    if (!was) {
      events.push({ action: 'Library document uploaded', target: d.name, details });
      return;
    }
    if (was.status !== d.status) {
      if (d.status === 'available') events.push({ action: 'Library document available', target: d.name, details });
      else if (d.status === 'uploading' && was.status === 'failed') events.push({ action: 'Library document upload retried', target: d.name, details: d.fileName });
      else if (d.status === 'failed') events.push({ action: 'Library document upload failed', target: d.name, details: 'Session expired during upload' });
    }
    if (!!was.archived !== !!d.archived) {
      events.push({ action: d.archived ? 'Library document archived' : 'Library document unarchived', target: d.name, details: d.fileName });
    }
  });

  // Scenarios switching on with `navy-assessments` are a baseline, not a save.
  if (!prev.scenarios || !next.scenarios) return events;
  const saved = byId(prev.scenarios);
  const fields = (s) => JSON.stringify([s.name, s.narrativeId, s.referenceKeyIds, s.sopIds]);
  next.scenarios.forEach((s) => {
    const was = saved.get(s.id);
    if (!was) {
      events.push({ action: 'Scenario created', target: s.name });
      return;
    }
    if (fields(was) !== fields(s)) events.push({ action: 'Scenario updated', target: s.name });
    if (!!was.archived !== !!s.archived) events.push({ action: s.archived ? 'Scenario archived' : 'Scenario unarchived', target: s.name });
  });
  return events;
}

/**
 * The assessments, before and after one change. `scenarioName` and
 * `referenceKey` read the scenario an assessment runs against. Processing
 * runs on its own, so its steps are the System's.
 */
export function assessmentEvents(prev, next, { scenarioName, referenceKey }) {
  const events = [];
  const before = byId(prev.assessments);
  next.assessments.forEach((a) => {
    const was = before.get(a.id);
    const where = `${scenarioName(a.scenarioId)} · ${a.fileName}`;
    if (!was) {
      events.push({ action: 'Assessment created', target: a.name, details: `${statusLabel(a.status)} · ${where}` });
      return;
    }
    if (was.status !== a.status) {
      const step = `${was.status}>${a.status}`;
      if (step === 'uploading>queued') events.push({ action: 'Assessment upload complete', target: a.name, details: `Queued · ${where}` });
      else if (step === 'queued>processing') events.push({ action: 'Assessment processing started', target: a.name, details: `Comparing against ${referenceKey(a.scenarioId)}`, actor: SYSTEM });
      else if (step === 'processing>completed') events.push({ action: 'Assessment processing complete', target: a.name, details: 'Transcript, comparison and report ready', actor: SYSTEM });
      else if (step === 'uploading>failed') events.push({ action: 'Assessment upload failed', target: a.name, details: a.error });
      else if (step === 'processing>failed') events.push({ action: 'Assessment processing failed', target: a.name, details: a.error, actor: SYSTEM });
      else if (step === 'failed>processing') events.push({ action: 'Assessment processing retried', target: a.name, details: where });
      else if (step === 'failed>uploading') events.push({ action: 'Assessment upload retried', target: a.name, details: where });
    }
    if (!!was.archived !== !!a.archived) {
      events.push({ action: a.archived ? 'Assessment archived' : 'Assessment unarchived', target: a.name, details: where });
    }
  });
  return events;
}

/* ---- Reading the log ------------------------------------------------------ */

/**
 * Entries in the date range and category that match the search. The search
 * reads what a row and its record show: the time as written, the category,
 * the action, and the record's details, context and author. The range counts
 * back from now; when the API lands, it decides the period, as for the
 * platform's Activity Log.
 */
export function filterActivities(entries, filters, now = Date.now()) {
  const term = filters.search.trim().toLowerCase();
  const since = now - Number(filters.range) * DAY;
  return entries.filter((entry) => {
    if (Date.parse(entry.time) < since) return false;
    if (filters.category !== 'all' && activityCategory(entry) !== filters.category) return false;
    if (!term) return true;
    return [formatTimestamp(entry.time, 'static'), activityCategory(entry), entry.action, entry.target, entry.details, entry.actor]
      .some((v) => String(v || '').toLowerCase().includes(term));
  });
}

export class ActivitiesModel {
  /**
   * `library` and `assessments` are the models whose changes it logs.
   * `actor` names the signed-in user, who did whatever the System didn't.
   * `now` exists so tests can fix the clock.
   */
  constructor(library, assessments, { entries, actor = EVALUATOR, now = () => Date.now() } = {}) {
    this.library = library;
    this.assessments = assessments;
    this.actor = actor;
    this.now = now;
    // The Library is usually built before `navy-assessments` resolves, with its
    // scenarios off; switched on, a new model takes the seeded ones, so the
    // trail is written against those.
    this.entries = entries || seedActivities({
      documents: library.getState().documents,
      scenarios: library.getState().scenarios || seedLibraryScenarios(),
      assessments: assessments.getState().assessments,
      now: now(),
    });
    this.filters = { ...DEFAULT_ACTIVITY_FILTERS };
    this.sort = { ...DEFAULT_ACTIVITY_SORT };
    this.usageScope = 'org';
    this.nextId = this.entries.length + 1;
    this.listeners = [];
    this.unwatch = null;
  }

  /** Starts logging the two models' transitions. Each snapshot is compared with the last. */
  watch() {
    if (this.unwatch) return;
    let library = this.library.getState();
    let assessments = this.assessments.getState();
    const offLibrary = this.library.subscribe((next) => {
      libraryEvents(library, next).forEach((e) => this.log(e.action, e.target, e.details, e.actor));
      library = next;
    });
    const offAssessments = this.assessments.subscribe((next) => {
      const { documents, scenarios } = this.library.getState();
      assessmentEvents(assessments, next, scenarioLookups(documents, scenarios || []))
        .forEach((e) => this.log(e.action, e.target, e.details, e.actor));
      assessments = next;
    });
    this.unwatch = () => { offLibrary(); offAssessments(); };
  }

  stop() {
    this.unwatch?.();
    this.unwatch = null;
  }

  /** One entry, now, by the signed-in user unless the caller says otherwise. The trail never breaks an action. */
  log(action, target, details, actor) {
    const entry = {
      id: `act${this.nextId++}`,
      time: new Date(this.now()).toISOString(),
      action,
      target,
      details,
      actor: actor || this.actor,
    };
    this.entries = [entry, ...this.entries];
    this.notify();
    return entry;
  }

  setActor(actor) {
    this.actor = actor || EVALUATOR;
  }

  setFilter(key, value) {
    if (!(key in this.filters)) return;
    this.filters = { ...this.filters, [key]: value };
    this.notify();
  }

  /** No results' way out: search and filters back to how the page opened; the sort stays. */
  clearSearchAndFilters() {
    this.filters = { ...DEFAULT_ACTIVITY_FILTERS };
    this.notify();
  }

  setSort(sort) {
    this.sort = sort;
    this.notify();
  }

  setUsageScope(scope) {
    if (!ACTIVITY_USAGE[scope]) return;
    this.usageScope = scope;
    this.notify();
  }

  getState() {
    return {
      entries: this.entries,
      filters: this.filters,
      sort: this.sort,
      usageScope: this.usageScope,
    };
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => { this.listeners = this.listeners.filter((l) => l !== listener); };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }
}
